// src/admin/pages/AdminMerchants.jsx
// First real data page in the admin panel -- pulls GET /admin/merchants/overview
// for the tier-breakdown cards, and GET /admin/merchants for the filterable
// list beneath it. Both calls run through adminApi (admin bearer token),
// completely separate from the merchant-facing lib/api.js instance.

import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import RequireAdmin from "@/admin/components/RequireAdmin";
import adminApi from "@/admin/lib/adminApi";
import MerchantDetailSheet from "@/admin/components/MerchantDetailSheet";
import AdminLayout from "@/admin/components/AdminLayout";

const PLAN_LABELS = { free: "Free", creator: "Creator", starter: "Starter", growth: "Growth", pro: "Pro" };

// Same helper as MerchantDetailSheet.jsx — built into every modern
// browser, no country-name library needed.
function countryLabel(code) {
  if (!code) return "—";
  const upper = code.toUpperCase();
  try {
    const name = new Intl.DisplayNames(["en"], { type: "region" }).of(upper);
    return name && name !== upper ? `${name} (${upper})` : upper;
  } catch {
    return upper;
  }
}

function OverviewCards({ overview }) {
  if (!overview) return null;
  const cards = [
    { label: "Total merchants", value: overview.total_merchants },
    { label: "Free", value: overview.by_plan.free },
    { label: "Creator", value: overview.by_plan.creator ?? 0 },
    { label: "Starter", value: overview.by_plan.starter },
    { label: "Growth", value: overview.by_plan.growth },
    { label: "Pro", value: overview.by_plan.pro },
    { label: "New this week", value: overview.new_this_week },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-7">
      {cards.map((c) => (
        <div key={c.label} className={`rounded-2xl border p-4 ${c.warn ? "border-hotpink/30 bg-hotpink/5" : "border-ink/10 bg-white"}`}>
          <p className="text-xs font-bold uppercase tracking-wide text-ink/50">{c.label}</p>
          <p className={`mt-1 font-display text-3xl tracking-tighter ${c.warn ? "text-hotpink" : ""}`}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function AdminMerchantsInner() {
  const [overview, setOverview] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planFilter, setPlanFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [detailMerchantId, setDetailMerchantId] = useState(null);

  const loadOverview = useCallback(async () => {
    const { data } = await adminApi.get("/admin/merchants/overview");
    setOverview(data);
  }, []);

  const loadMerchants = useCallback(async () => {
    const { data } = await adminApi.get("/admin/merchants", {
      params: { plan: planFilter, search, page, page_size: 25 },
    });
    setMerchants(data.merchants);
    setPagination(data.pagination);
  }, [planFilter, search, page]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    setLoading(true);
    loadMerchants().finally(() => setLoading(false));
  }, [loadMerchants]);

  function handlePlanFilterChange(plan) {
    setPlanFilter(plan);
    setPage(1);
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-5xl">
        <h1 className="font-display text-3xl tracking-tighter">Merchants</h1>

        <div className="mt-6">
          <OverviewCards overview={overview} />
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <select
            value={planFilter}
            onChange={(e) => handlePlanFilterChange(e.target.value)}
            className="rounded-xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-hotpink"
          >
            <option value="">All plans</option>
            {Object.entries(PLAN_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name..."
            className="min-w-[220px] flex-1 rounded-xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-hotpink"
          />
        </div>

        <div className="mt-4 overflow-hidden rounded-2xl border border-ink/10 bg-white">
          {loading ? (
            <div className="grid place-items-center p-10">
              <Loader2 className="h-5 w-5 animate-spin text-ink/40" />
            </div>
          ) : merchants.length === 0 ? (
            <p className="p-8 text-center text-sm text-ink/50">No merchants match this filter.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-ink/10 bg-cream/60 text-xs font-bold uppercase tracking-wide text-ink/50">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">IG Handle</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {merchants.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => setDetailMerchantId(m.id)}
                    className="cursor-pointer border-b border-ink/5 transition-colors last:border-0 hover:bg-cream/60"
                  >
                    <td className="px-4 py-3 font-semibold">{m.name}</td>
                    <td className="px-4 py-3 text-ink/60">
                      {m.ig_username ? `@${m.ig_username}` : "—"}
                    </td>
                    <td className="px-4 py-3">{PLAN_LABELS[m.plan] || m.plan}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          m.subscription_status === "active"
                            ? "bg-mint/60 text-ink"
                            : "bg-peach/60 text-ink"
                        }`}
                      >
                        {m.subscription_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink/60">{countryLabel(m.country)}</td>
                    <td className="px-4 py-3 text-ink/60">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm text-ink/60">
            <span>
              Page {pagination.page} of {pagination.total_pages} &middot; {pagination.total_count} total
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border border-ink/15 px-3 py-1.5 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={page >= pagination.total_pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-ink/15 px-3 py-1.5 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <MerchantDetailSheet
        open={!!detailMerchantId}
        onOpenChange={(v) => !v && setDetailMerchantId(null)}
        merchantId={detailMerchantId}
      />
    </AdminLayout>
  );
}

export default function AdminMerchants() {
  return (
    <RequireAdmin>
      <AdminMerchantsInner />
    </RequireAdmin>
  );
}
